import { ExcelCellUtils } from './ExcelCelUtils';

interface ExcelLoadeable {
    context: Excel.RequestContext;
    load(propertyNames?: string | string[]): this;
}

export type ExcelBatch<T> = (context: Excel.RequestContext, workbook: Excel.Workbook) => Promise<T> | T;

/**
 * A proxy object installed for the Excel context, which intercepts the sync
 * calls and automatically activates all 'silent' modes.
 */
const SilentModeReactivationSyncHandler: ProxyHandler<Excel.RequestContext> = {
    get(target: Excel.RequestContext, p: string | symbol, receiver: any): any {
        if (p === 'isSilentProxy') {
            return true;
        } else if (p === 'sync') {
            return async () => {
                const app = target.application;
                const result = await target.sync();
                app.suspendScreenUpdatingUntilNextSync();
                app.suspendApiCalculationUntilNextSync();
                return result;
            }
        } else {
            return (target as any)[p];
        }
    }
};

const SilentModeWorkbookSyncWrapper : ProxyHandler<Excel.Workbook> = {
    get(target: Excel.Workbook, p: string | symbol, receiver: any): any {
        const val = (target as any)[p];
        if (p === 'context' && !val.isSilentProxy) {
            return new Proxy(val, SilentModeReactivationSyncHandler);
        } else {
            return val;
        }
    }
}


export class ExcelUtils {

    static async run<T> (callback: ExcelBatch<T>): Promise<T> {
        try {
            return await Excel.run({delayForCellEdit: true}, async (context) => {
                context.application.suspendApiCalculationUntilNextSync();
                context.application.suspendScreenUpdatingUntilNextSync();
                try {
                    const result = callback(context, context.workbook);
                    if (result instanceof Promise) {
                        return await result;
                    } else {
                        return result;
                    }
                } finally {
                    context.application.calculationMode = Excel.CalculationMode.automatic;
                }
            });
        } catch (error) {
            ExcelUtils.logError(error);
            throw error;
        }
    }

    private static logError(error: any) {
        console.error('Error: ' + error);
        if (error instanceof OfficeExtension.Error) {
            // console.error(error, "Debug info: " + JSON.stringify(error.debugInfo));
            if (error.debugInfo?.fullStatements?.length ?? 0 > 1) {
                console.error(`Error: ${error.message}\nCode : ${error.code}`);
                console.error(`Location: ${error.debugInfo.errorLocation}\nCode : ${error.code}`);
                console.error(`Full statements:\n${error.debugInfo?.fullStatements?.join('\n')}`)
            } else {
                console.error('Debug info: ' + JSON.stringify(error.debugInfo));
            }
        }
    }

    static async runFastAndSilent<T> (callback: (context: Excel.RequestContext, workbook: Excel.Workbook) => Promise<T>): Promise<T> {
        return await Excel.run(async (context) => {
            const app = context.workbook.application;
            // app.load("calculationMode");
            // await context.sync();
            // const calcMode = app.calculationMode;

            try {
                app.calculationMode = Excel.CalculationMode.manual;
                return await callback(new Proxy(context, SilentModeReactivationSyncHandler), context.workbook);
                // return await callback(context, context.workbook);
            } catch (error) {
                console.error('Error: ' + error);
                if (error instanceof OfficeExtension.Error) {
                    console.log('Debug info: ' + JSON.stringify(error.debugInfo));
                }
                throw error;
            } finally {
                app.calculationMode = Excel.CalculationMode.automatic;
            }
        });
    }

    static inUnprotected<T>(sheet: Excel.Worksheet, callback: () => T): T {
        const isProtected = true;
        try {
            if (isProtected) sheet.protection.unprotect();
            return callback();
        } finally {
            if (isProtected) sheet.protection.protect({
                selectionMode: Excel.ProtectionSelectionMode.unlocked,
                allowAutoFilter: true,
            });
        }
    }

    static async inUnprotectedAsync<T>(sheet: Excel.Worksheet, callback: () => Promise<T>): Promise<T> {
        // const isProtected = sheet.protection;
        const isProtected = true;
        try {
            if (isProtected) {
                sheet.protection.unprotect();
            }
            return await callback();
        } finally {
            if (isProtected) sheet.protection.protect({
                selectionMode: Excel.ProtectionSelectionMode.unlocked,
                allowAutoFilter: true,
            });
        }
    }

    static async runWithAllShetsUnprotected(workbook: Excel.Workbook, callback: () => Promise<void>): Promise<void> {
        const worksheets = workbook.worksheets;
        const originalProtectionStates: boolean[] = [];

        // Unprotect all sheets
        for (const sheet of worksheets.items) {
            originalProtectionStates.push(sheet.protection.protected);
            sheet.protection.unprotect();
        }

        try {
            await callback();
        } finally {
            // Restore original protection states
            for (let i = 0; i < worksheets.items.length; i++) {
                if (originalProtectionStates[i]) {
                    worksheets.items[i].protection.protect({
                        selectionMode: Excel.ProtectionSelectionMode.unlocked,
                        allowAutoFilter: true,
                    });
                }
            }
        }
    }

    static preloadWorksheetBaseProperties(workbook: Excel.Workbook): void {
        workbook.worksheets.load('items,items/protection,items/name,items/protection/protected');
    }

    static async loadAndSync<V extends ExcelLoadeable>(item: V, ...properties: string[]): Promise<V> {
        item.load(...properties);
        await item.context.sync();
        return item;
    }

    /**
     * Returns a range by either its name or its address. Equivalent to VBA '[....]' operator.
     * @param workbook        activate workbook
     * @param addressOrName   adress or name of range
     */
    static getRange(workbook: Excel.Workbook, addressOrName: string): Excel.Range {
        const parsedRange = ExcelCellUtils.parseRangeAddress(addressOrName);
        if (parsedRange != null) {
            // we can't use Excel worksheet's logic to resolve absolute address because
            // they will work only via the correct worksheet. hence, we have to parse the worksheet name
            return workbook.worksheets.getItemOrNullObject(parsedRange.sheetName).getRange(parsedRange.cellAddress);
        } else {
            return ExcelUtils.getNamedRange(workbook, addressOrName);
        }
    }

    static getNamedRange(workbook: Excel.Workbook, name: string): Excel.Range {
        const namedItem = workbook.names.getItemOrNullObject(name);
        return namedItem.getRangeOrNullObject();
    }

    static async getNamedRangeSync(workbook: Excel.Workbook, name: string): Promise<Excel.Range | null> {
        const namedItem = workbook.names.getItemOrNullObject(name);
        const rangeObject = namedItem.getRangeOrNullObject();

        await workbook.context.sync();
        return (rangeObject.isNullObject) ? null : rangeObject;
    }

    static forceRangeRecalculation(range: Excel.Range): Promise<Excel.Range> {
        return new Promise(async (resolve, reject) => {
            try {
                // attach a one-time listener to the worksheet, to recieve the notification when
                // the re-calculation of the sheet is finished
                const calcListener = range.worksheet.onCalculated.add(async (args) => {
                    // make sure, this handler is no longer active
                    calcListener.remove();
                    resolve(range);
                });
                range.calculate();
                await range.context.sync();
            } catch (e) {
                reject(e);
            }
        });
    }

    static async refreshDocumentSettings(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            try { Office.context.document.settings.refreshAsync(() => resolve()); }
            catch (e) {
                console.error(e);
                reject(e);
            }
        });
    }

    static async saveDocumentSettings(): Promise<void> {
        return new Promise((resolve, reject) => {
            Office.context.document.settings.saveAsync(() => {
                try {
                    console.log('Office document settings saved');
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        });
    }
}
